import { eventsFlux, EventTypes, nodesFlux, ROOT_NODE, useEditorSelector } from '@candulabs/craft';
import React, { useEffect } from 'react';
import { createStructuredSelector } from 'reselect';

import { Flags, useFlags } from 'commons/hooks';
import { CraftMode } from 'craft/containers/Craft/Craft';
import { TOOLBOX_DOCK_ID } from 'craft/containers/Toolbox/constants';
import { InlineSnapshotViewport } from 'craft/containers/Viewport/modes/InlineSnapshotViewport';
import { NoSnapshotViewport } from 'craft/containers/Viewport/modes/NoSnapshotViewport';
import { SnapshotViewport } from 'craft/containers/Viewport/modes/SnapshotViewport';
import { EditorLockOverlay } from 'craft/containers/Viewport/modes/components';
import { useEditor } from 'craft/hooks';
import { useCurrentSnapshot } from 'craft/state';
import { useViewportDom } from 'craft/state/viewport/selectors';
import { isDomElement } from 'craft/utils/viewport';

const viewportStructuredSelector = createStructuredSelector({
  isRootSelected: eventsFlux.selectors.doesNodeHaveEvent(ROOT_NODE, EventTypes.selected),
  rootNodeName: nodesFlux.selectors.getNodeName(ROOT_NODE),
});

export interface ViewportProps {
  children: React.ReactNode;
  contentId: string;
  editIterator: number;
  mode: CraftMode;
}

export const Viewport = React.memo(({ children, contentId, editIterator, mode }: ViewportProps) => {
  const { isRootSelected, rootNodeName } = useEditorSelector(viewportStructuredSelector);
  const { snapshot } = useCurrentSnapshot();
  const { viewportWrapperEl } = useViewportDom();
  const { actions } = useEditor();

  const { [Flags.DOCKED_TOOLBOX]: dockedToolbox } = useFlags();

  useEffect(() => {
    if (!viewportWrapperEl || !dockedToolbox) {
      return () => undefined;
    }

    const onMouseLeave = (e: MouseEvent) => {
      if (isDomElement(e.relatedTarget) && !!e.relatedTarget.closest(`#${TOOLBOX_DOCK_ID}`)) {
        // if event is triggered by interacting with the docked toolbox, do nothing
        return;
      }
      actions.setNodeEvent(EventTypes.hovered, []);
    };

    // when mouse leaves the canvas, reset hovered nodes to hide the toolbox dock
    viewportWrapperEl.addEventListener('mouseleave', onMouseLeave);

    return () => {
      viewportWrapperEl.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [viewportWrapperEl, dockedToolbox]);

  if (!snapshot) {
    return (
      <>
        <NoSnapshotViewport
          isRootSelected={isRootSelected}
          contentId={contentId}
          rootNodeName={rootNodeName}
        >
          {children}
        </NoSnapshotViewport>
        <EditorLockOverlay mode={mode} />
      </>
    );
  }

  if (snapshot && rootNodeName === 'Document') {
    return (
      <>
        <InlineSnapshotViewport
          isRootSelected={isRootSelected}
          editIterator={editIterator}
          contentId={contentId}
          rootNodeName={rootNodeName}
        >
          {children}
        </InlineSnapshotViewport>
        <EditorLockOverlay mode={mode} />
      </>
    );
  }

  return (
    <>
      <SnapshotViewport
        isRootSelected={isRootSelected}
        contentId={contentId}
        rootNodeName={rootNodeName}
      >
        {children}
      </SnapshotViewport>
      <EditorLockOverlay mode={mode} />
    </>
  );
});
